import * as THREE from 'three';
import { t } from './translations';


const AR_SCALE_KEY     = 'ar_map_ar_scale';
const AR_SCALE_DEFAULT = 0.008;
const AR_SCALE_MIN     = 0.001;
const AR_SCALE_MAX     = 0.08;

const AR_OFFSET_KEY     = 'ar_map_ar_offset';
const AR_OFFSET_DEFAULT = 0;
const AR_OFFSET_MIN     = 0;
const AR_OFFSET_MAX     = 1.0;

export class ARController {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    session: XRSession | null = null;
    hitTestSource: XRHitTestSource | null = null;
    refSpace: XRReferenceSpace | null = null;

    arScale: number;
    arOffset: number;
    reticle: THREE.Mesh;
    onExited: (() => void) | null = null;

    private _worldRoot: THREE.Object3D | null = null;
    private _lastHitMatrix: THREE.Matrix4 | null = null;
    private _suppressSelect = false;
    private _placed    = false;
    private _controls:  { enabled: boolean } | null = null;
    private _background: THREE.Color | null = null;
    private _animateCallback: ((t: number, frame: XRFrame | null) => void) | null = null;
    private _placedPos  = new THREE.Vector3();
    private _pinch0: number | null = null;
    private _scaleBase: number;
    private _forcedEnd  = false;
    private _closeArPanels: (() => void) | null = null;
    private _prevClearColor = new THREE.Color();
    private _prevClearAlpha = 1;

    constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene) {
        this.renderer = renderer;
        this.scene    = scene;
        this.arScale  = parseFloat(localStorage.getItem(AR_SCALE_KEY) ?? '0') || AR_SCALE_DEFAULT;
        this.arOffset = parseFloat(localStorage.getItem(AR_OFFSET_KEY) ?? '0') || AR_OFFSET_DEFAULT;
        this._scaleBase = this.arScale;

        this.reticle = this._createReticle();
        this.reticle.visible = false;
        scene.add(this.reticle);
    }

    setAnimateCallback(cb: (t: number, frame: XRFrame | null) => void): void {
        this._animateCallback = cb;
    }

    private _createReticle(): THREE.Mesh {
        const geo = new THREE.RingGeometry(0.07, 0.09, 36);
        geo.rotateX(-Math.PI / 2);
        const mat = new THREE.MeshBasicMaterial({
            color: 0x7fb3d3, side: THREE.DoubleSide, depthWrite: false,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.matrixAutoUpdate = false;
        return mesh;
    }

    static async isSupported(): Promise<boolean> {
        try { return !!(await navigator.xr?.isSessionSupported('immersive-ar')); }
        catch { return false; }
    }

    get active(): boolean { return !!this.session; }
    get forcedEnd(): boolean { return this._forcedEnd; }

    private _scaleToSlider(s: number): number {
        return 100 * Math.log(s / AR_SCALE_MIN) / Math.log(AR_SCALE_MAX / AR_SCALE_MIN);
    }
    private _sliderToScale(v: number): number {
        return AR_SCALE_MIN * Math.pow(AR_SCALE_MAX / AR_SCALE_MIN, v / 100);
    }
    private _offsetToSlider(o: number): number {
        return 100 * (o - AR_OFFSET_MIN) / (AR_OFFSET_MAX - AR_OFFSET_MIN);
    }
    private _sliderToOffset(v: number): number {
        return AR_OFFSET_MIN + (v / 100) * (AR_OFFSET_MAX - AR_OFFSET_MIN);
    }

    setScale(val: number): void {
        this.arScale = Math.max(AR_SCALE_MIN, Math.min(AR_SCALE_MAX, val));
        this._worldRoot?.scale.setScalar(this.arScale);
        localStorage.setItem(AR_SCALE_KEY, String(this.arScale));
        this._updateLabels();
    }

    setOffset(val: number): void {
        this.arOffset = Math.max(AR_OFFSET_MIN, Math.min(AR_OFFSET_MAX, val));
        localStorage.setItem(AR_OFFSET_KEY, String(this.arOffset));
        if (this._placed && this._worldRoot) {
            this._worldRoot.position.y = this._placedPos.y + this.arOffset;
        }
        this._updateLabels();
    }

    adjustScale(factor: number): void { this.setScale(this.arScale * factor); }

    private _updateLabels(): void {
        const wm = this.arScale * 100;
        const wLabel = wm >= 1 ? wm.toFixed(1) + ' m' : (wm * 100).toFixed(0) + ' cm';
        const sLabel = document.getElementById('ar-scale-label');
        if (sLabel) sLabel.textContent = wLabel;

        const oCm = (this.arOffset * 100).toFixed(0);
        const oLabel = document.getElementById('ar-offset-label');
        if (oLabel) oLabel.textContent = oCm + ' cm';

        const ss = document.getElementById('ar-scale-slider') as HTMLInputElement | null;
        if (ss) ss.value = String(this._scaleToSlider(this.arScale));
        const os = document.getElementById('ar-offset-slider') as HTMLInputElement | null;
        if (os) os.value = String(this._offsetToSlider(this.arOffset));
    }

    // Wires the AR-only placement sliders (Size/Height). Independent of the
    // Map/Data/Settings panel toggling, which main.ts owns generically.
    initSettingsUI(): void {
        (document.getElementById('ar-scale-slider') as HTMLInputElement | null)
            ?.addEventListener('input', e =>
                this.setScale(this._sliderToScale(parseFloat((e.target as HTMLInputElement).value))));
        (document.getElementById('ar-offset-slider') as HTMLInputElement | null)
            ?.addEventListener('input', e =>
                this.setOffset(this._sliderToOffset(parseFloat((e.target as HTMLInputElement).value))));
        this._updateLabels();
    }

    // main.ts registers a callback that closes whichever AR side-panel
    // (Map/Data/Settings) is currently open, so re-placing the model also
    // tidies up the UI.
    setClosePanelsCallback(cb: () => void): void {
        this._closeArPanels = cb;
    }

    // worldRoot is the single group that contains terrain, POIs, labels, tiles.
    // In AR it is repositioned and scaled; in desktop it sits at the scene origin.
    async enter(
        worldRoot: THREE.Object3D,
        controls: { enabled: boolean },
        background: THREE.Color,
    ): Promise<void> {
        let session: XRSession;
        try {
            session = await navigator.xr!.requestSession('immersive-ar', {
                requiredFeatures: ['hit-test'],
                optionalFeatures: ['dom-overlay', 'local-floor'],
                domOverlay: { root: document.getElementById('ar-overlay')! },
            });
        } catch (e) {
            alert('Could not start AR: ' + (e as Error).message);
            return;
        }

        this.session     = session;
        this._worldRoot  = worldRoot;
        this._controls   = controls;
        this._background = background;
        controls.enabled = false;
        session.addEventListener('end', () => this._cleanup());

        try {
            // Probe which reference space the session actually granted before
            // telling Three.js which type to use — setReferenceSpaceType must
            // match what the session supports or setSession will throw.
            let refSpaceType: XRReferenceSpaceType = 'local';
            let refSpace: XRReferenceSpace;
            try {
                refSpace = await session.requestReferenceSpace('local-floor');
                refSpaceType = 'local-floor';
            } catch {
                refSpace = await session.requestReferenceSpace('local');
            }

            this.renderer.xr.setReferenceSpaceType(refSpaceType);
            await this.renderer.xr.setSession(session);

            // Scale and hide worldRoot until the user taps to place it.
            worldRoot.scale.setScalar(this.arScale);
            worldRoot.visible = false;
            this.scene.background = null;

            // The renderer normally clears to an opaque colour, which paints
            // over the passthrough camera feed the XR compositor provides.
            // Drop the clear alpha to 0 for the duration of the session so
            // untouched pixels stay transparent and reveal the camera.
            this.renderer.getClearColor(this._prevClearColor);
            this._prevClearAlpha = this.renderer.getClearAlpha();
            this.renderer.setClearColor(this._prevClearColor, 0);
            document.documentElement.classList.add('xr-active');

            this.refSpace     = refSpace;
            const viewerSpace = await session.requestReferenceSpace('viewer');
            this.hitTestSource = (await session.requestHitTestSource?.({ space: viewerSpace })) ?? null;

            session.addEventListener('select', () => {
                if (!this._suppressSelect) this._onSelect();
            });

            this._placed = false;
            this._setState('placing');
        } catch (e) {
            console.error('AR session setup failed:', e);
            alert('AR setup failed: ' + (e as Error).message);
            session.end();
        }
    }

    onFrame(frame: XRFrame): void {
        if (!frame || !this.hitTestSource || !this.refSpace) return;
        const hits = frame.getHitTestResults(this.hitTestSource);
        if (hits.length > 0) {
            const pose = hits[0].getPose(this.refSpace);
            if (pose) {
                this._lastHitMatrix = new THREE.Matrix4().fromArray(pose.transform.matrix);
                if (!this._placed) {
                    this.reticle.visible = true;
                    this.reticle.matrix.copy(this._lastHitMatrix);
                }
            }
        } else if (!this._placed) {
            this.reticle.visible = false;
        }
    }

    private _onSelect(): void {
        if (this._placed || !this._lastHitMatrix || !this._worldRoot) return;
        const pos  = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scl  = new THREE.Vector3();
        this._lastHitMatrix.decompose(pos, quat, scl);

        this._placedPos.copy(pos);
        this._worldRoot.position.set(pos.x, pos.y + this.arOffset, pos.z);
        this._worldRoot.quaternion.copy(quat);
        this._worldRoot.visible = true;
        this._placed = true;
        this.reticle.visible = false;
        this._setState('placed');
    }

    rePlace(): void {
        this._suppress();
        this._placed = false;
        if (this._worldRoot) this._worldRoot.visible = false;
        this._closeArPanels?.();
        this._setState('placing');
    }

    exit(): void { this.session?.end(); }

    handleTouchStart(e: TouchEvent): void {
        if (e.touches.length === 2) {
            this._suppress();
            this._pinch0    = this._touchDist(e);
            this._scaleBase = this.arScale;
        }
    }

    handleTouchMove(e: TouchEvent): void {
        if (e.touches.length !== 2 || this._pinch0 === null) return;
        this.setScale(this._scaleBase * (this._touchDist(e) / this._pinch0));
    }

    handleTouchEnd(e: TouchEvent): void {
        if (e.touches.length < 2) this._pinch0 = null;
    }

    private _suppress(): void {
        this._suppressSelect = true;
        setTimeout(() => { this._suppressSelect = false; }, 400);
    }

    private _setState(state: 'placing' | 'placed'): void {
        document.getElementById('ar-overlay')!.classList.remove('hidden');
        const hint = document.getElementById('ar-hint')!;
        if (state === 'placing') {
            hint.textContent = t('arHint');
            hint.classList.remove('hidden');
        } else {
            hint.classList.add('hidden');
        }

        const show = (id: string) => document.getElementById(id)!.classList.remove('hidden');
        const hide = (id: string) => document.getElementById(id)!.classList.add('hidden');

        hide('ar-replace-btn');
        document.getElementById('ar-icon-row')?.classList.add('hidden');

        if (state === 'placed') {
            show('ar-replace-btn');
            document.getElementById('ar-icon-row')?.classList.remove('hidden');
        } else {
            this._closeArPanels?.();
        }
    }

    private _cleanup(): void {
        this._forcedEnd = document.visibilityState === 'hidden';
        this.hitTestSource?.cancel();
        this.hitTestSource = null;
        this.reticle.visible = false;
        this._closeArPanels?.();

        // Reset worldRoot to desktop defaults — no objects need to move.
        if (this._worldRoot) {
            this._worldRoot.position.set(0, 0, 0);
            this._worldRoot.quaternion.identity();
            this._worldRoot.scale.set(1, 1, 1);
            this._worldRoot.visible = true;
            this._worldRoot = null;
        }

        if (this._controls) this._controls.enabled = true;
        this.scene.background = this._background ?? new THREE.Color(0x7aaecc);
        this.renderer.setClearColor(this._prevClearColor, this._prevClearAlpha);
        document.documentElement.classList.remove('xr-active');
        this.session = null;
        this._placed = false;
        document.getElementById('ar-overlay')!.classList.add('hidden');

        if (this._animateCallback) {
            const cb = this._animateCallback;
            Promise.resolve().then(() => this.renderer.setAnimationLoop(cb));
        }
        this.onExited?.();
    }

    private _touchDist(e: TouchEvent): number {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
}
